"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getFilteredEligibility = exports.getFilteredPunchesEligibilityWithTimeRange = exports.getSelectedDatePunchTimeSegment = exports.getSelectedPunchCorrections = exports.PunchFeature = exports.isWithinPrevAndCurrPayPeriod = void 0;
const react_1 = __importDefault(require("react"));
const luxon_1 = require("luxon");
const PunchSection_1 = require("../../components/PunchSection");
const hooks_1 = require("../../hooks");
const DateManagement_1 = require("../../utils/DateManagement");
const MealBreakLockoutValidation_1 = require("../../utils/MealBreakLockoutValidation");
const utils_1 = require("../../utils");
const logger_1 = require("../../logger");
const TAAErrorBoundary_1 = require("../../components/Shared/TAAErrorBoundary");
const useUnifiedPunches_1 = require("../../hooks/useUnifiedPunches");
const consts_1 = require("../../consts");
const contexts_1 = require("../../contexts");
const isWithinPrevAndCurrPayPeriod = (selectedDate, payPeriodBreakdownInfo) => {
    var _a;
    const intervalStart = ((_a = payPeriodBreakdownInfo === null || payPeriodBreakdownInfo === void 0 ? void 0 : payPeriodBreakdownInfo.previous) === null || _a === void 0 ? void 0 : _a.start_time)
        ? luxon_1.DateTime.fromISO(payPeriodBreakdownInfo.previous.start_time)
        : luxon_1.DateTime.now().minus({ days: 14 });
    const interval = luxon_1.Interval.fromDateTimes(intervalStart, luxon_1.DateTime.now());
    return interval.contains(selectedDate);
};
exports.isWithinPrevAndCurrPayPeriod = isWithinPrevAndCurrPayPeriod;
const PunchFeature = ({ canDeleteDuplicatePunch, ctkPolicy, hasUnverifiedMissedPunchLimit, punchAutomationPolicy, militaryTime, selectedDateTime, onRedirect, associateInfo: associateInfoInProps, isInTimecard = false, featureGates = {
    isDeleteDupePunchEnabled: false,
    isPunchCorrectionEnabled: false,
    isPunchCorrStatusEnabled: false,
    isSubmitMissedPunchV2Enabled: false,
    isPunchEditForNativeEnabled: false,
    isPunchEditForWebEnabled: false,
    isMissedPunchClickableRowForWebEnabled: false,
    isMissedPunchClickableRowForNativeEnabled: false,
    useUnifiedPunchesAPIForWeb: false,
    useUnifiedPunchesAPIForNative: false,
    isPunchCorrStatusV1Enabled: false,
    canSubmitMissedPunch: false,
    ctkPolicy: false,
    hasUnverifiedMissedPunchLimit: false,
    isTimecardModalEnabled: false,
    isTimeCardPunchWebEnabled: false,
    isTimeCardPunchNativeEnabled: false,
    isTimeCardTaskListWebEnabled: false,
    isTimeCardTaskListNativeEnabled: false,
    isAnomalySearchEnabled: false,
    isATPlusTemporalRoutingEnabled: false
}, clientType, fesPolicyMissingFG, fesPolicyMissingFGLogs }) => {
    var _a, _b, _c, _d;
    const { associateInfo: associateInfoInContext } = (0, contexts_1.useTaaConfigsContext)();
    const associateInfo = associateInfoInContext || associateInfoInProps;
    (0, DateManagement_1.setTimeZone)(associateInfo.timeZone);
    const { selectedDate, timeRange } = (0, hooks_1.useDateQueryParams)(selectedDateTime);
    const usageLocation = consts_1.featureNames.PUNCH_FEATURE;
    const { clientName } = (0, contexts_1.useTaaConfigsContext)();
    const dimensions = {
        ClientName: clientName,
        ClientType: clientType || logger_1.ClientType.WEB
    };
    if (fesPolicyMissingFG) {
        fesPolicyMissingFGLogs.map((fesPolicyMissingFGLog) => {
            logger_1.logger.emitCountMetric({
                metricName: fesPolicyMissingFGLog.missingFeature +
                    logger_1.METRICS.FES_POLICY_MISSING_FEATURE_COUNT.COUNT,
                dimensions: Object.assign(Object.assign({}, dimensions), { featureGates: fesPolicyMissingFGLog.fesPolicyName })
            });
            logger_1.logger.info(`FESPolicyMissingFG: for ${associateInfo.employeeId}, 
        they are missing the feature ${fesPolicyMissingFGLog.missingFeature}`);
        });
    }
    const { isLoading: isPunchesLoading, data } = (0, hooks_1.usePunches)({
        employeeId: associateInfo.employeeId,
        timeRange,
        isEligibilityEnabled: canDeleteDuplicatePunch || featureGates.isDeleteDupePunchEnabled,
        usageLocation,
        temporalRouting: featureGates === null || featureGates === void 0 ? void 0 : featureGates.isATPlusTemporalRoutingEnabled
    });
    const { isLoading: isUndefinedPunchesLoading, data: { timespanPunchGroups } = {} } = (0, useUnifiedPunches_1.useUnifiedPunches)({
        employeeId: associateInfo.employeeId,
        startTimestamp: timeRange.startWeek,
        endTimestamp: timeRange.endWeek,
        timezone: associateInfo.timeZone,
        shouldFetch: featureGates.useUnifiedPunchesAPIForWeb ||
            featureGates.useUnifiedPunchesAPIForNative,
        usageLocation
    });
    const { isLoading: isPayPeriodsLoading, data: { pay_period_breakdown_info } = {} } = (0, hooks_1.usePayPeriod)({
        employeeId: associateInfo.employeeId,
        shouldFetch: canDeleteDuplicatePunch ||
            punchAutomationPolicy ||
            featureGates.isDeleteDupePunchEnabled ||
            featureGates.isPunchCorrectionEnabled,
        usageLocation
    });
    const { isLoading: isPunchCorrectionsLoading, data: { punchCorrections } = {}, refetch: refetchPunchCorrections } = (0, hooks_1.usePunchCorrections)({
        employeeId: associateInfo.employeeId,
        timeRange: {
            start: (_a = pay_period_breakdown_info === null || pay_period_breakdown_info === void 0 ? void 0 : pay_period_breakdown_info.current.start_time) !== null && _a !== void 0 ? _a : '',
            end: (_b = pay_period_breakdown_info === null || pay_period_breakdown_info === void 0 ? void 0 : pay_period_breakdown_info.current.end_time) !== null && _b !== void 0 ? _b : ''
        },
        shouldFetch: (punchAutomationPolicy || featureGates.isPunchCorrectionEnabled) &&
            !!(pay_period_breakdown_info === null || pay_period_breakdown_info === void 0 ? void 0 : pay_period_breakdown_info.current),
        usageLocation
    });
    const { isLoading: isPunchCorrStatusListLoading, data: { punchCorrections: punchCorrStatusList = [] } = {}, refetch: fetchPunchCorrStatusList } = (0, hooks_1.usePunchCorrStatusList)({
        enabled: featureGates.isPunchEditForNativeEnabled ||
            featureGates.isPunchEditForWebEnabled,
        employeeId: associateInfo.employeeId,
        timeRange: {
            start: luxon_1.DateTime.now().minus({ days: 6 }).startOf('day').toUTC().toISO(),
            end: luxon_1.DateTime.now().endOf('day').toUTC().toISO()
        },
        useV1Api: featureGates.isPunchCorrStatusV1Enabled,
        usageLocation
    });
    const filteredPunches = (0, exports.getFilteredPunchesEligibilityWithTimeRange)(canDeleteDuplicatePunch || featureGates.isDeleteDupePunchEnabled, selectedDate, data === null || data === void 0 ? void 0 : data.punchesTimeSegments, {
        start: (_c = pay_period_breakdown_info === null || pay_period_breakdown_info === void 0 ? void 0 : pay_period_breakdown_info.current.start_time) !== null && _c !== void 0 ? _c : '',
        end: (_d = pay_period_breakdown_info === null || pay_period_breakdown_info === void 0 ? void 0 : pay_period_breakdown_info.current.end_time) !== null && _d !== void 0 ? _d : ''
    });
    const targetPunches = featureGates.useUnifiedPunchesAPIForWeb ||
        featureGates.useUnifiedPunchesAPIForNative
        ? (0, utils_1.findTargetPunchesWithUnifiedPunches)(selectedDate, timespanPunchGroups, pay_period_breakdown_info)
        : null;
    const punchesForMealLockoutValdn = (0, MealBreakLockoutValidation_1.getPunchesForMealLockoutValdn)(timespanPunchGroups, selectedDate);
    const filteredPunchCorrections = (0, exports.getSelectedPunchCorrections)(punchCorrections, selectedDate);
    const isDataLoading = isPunchesLoading ||
        isPayPeriodsLoading ||
        isPunchCorrectionsLoading ||
        isUndefinedPunchesLoading ||
        ((featureGates.isPunchEditForNativeEnabled ||
            featureGates.isPunchEditForWebEnabled) &&
            isPunchCorrStatusListLoading);
    (0, logger_1.useComponentLoadedMetrics)({
        metricNames: {
            countMetricName: logger_1.METRICS.PUNCH_FEATURE.COUNT,
            latencyMetricName: logger_1.METRICS.PUNCH_FEATURE.LATENCY
        },
        isComponentLoaded: !isDataLoading
    });
    return (react_1.default.createElement(TAAErrorBoundary_1.TAAErrorBoundary, null,
        react_1.default.createElement(PunchSection_1.PunchSection, { isDataLoading: isDataLoading, isInTimecard: isInTimecard, punchesTimeSegments: filteredPunches, punchWithTimespans: targetPunches, punchesForMealLockoutValdn: punchesForMealLockoutValdn, punchCorrections: filteredPunchCorrections, ctkPolicy: ctkPolicy || featureGates.ctkPolicy, hasUnverifiedMissedPunchLimit: hasUnverifiedMissedPunchLimit ||
                featureGates.hasUnverifiedMissedPunchLimit, isPunchCorrectionEnabled: punchAutomationPolicy || featureGates.isPunchCorrectionEnabled, isSubmitMissedPunchV2Enabled: featureGates.isSubmitMissedPunchV2Enabled, isPunchCorrStatusEnabled: featureGates.isPunchCorrStatusEnabled, useUnifiedPunchesAPIForWeb: featureGates.useUnifiedPunchesAPIForWeb, useUnifiedPunchesAPIForNative: featureGates.useUnifiedPunchesAPIForNative, showRoundedPunch: featureGates.showRoundedPunch, canSubmitMissedPunch: featureGates.canSubmitMissedPunch &&
                (0, exports.isWithinPrevAndCurrPayPeriod)(selectedDate, pay_period_breakdown_info), canDeleteDuplicatePunch: canDeleteDuplicatePunch, militaryTime: militaryTime, selectedDate: selectedDate, onRedirect: onRedirect, associateInfo: associateInfo, refetchPunchCorrections: () => {
                if (refetchPunchCorrections) {
                    refetchPunchCorrections();
                }
            }, isPunchCorrStatusListLoading: isPunchCorrStatusListLoading, punchCorrStatusList: punchCorrStatusList, fetchPunchCorrStatusList: fetchPunchCorrStatusList, featureGates: featureGates, payPeriodBreakdownInfo: pay_period_breakdown_info })));
};
exports.PunchFeature = PunchFeature;
const getSelectedPunchCorrections = (punchCorrections = [], selectedDate) => {
    const filteredPunchCorrections = punchCorrections.filter((punchCorrection) => (0, DateManagement_1.areDatesEqual)(selectedDate, luxon_1.DateTime.fromISO(punchCorrection.correctionDateTime)));
    return filteredPunchCorrections;
};
exports.getSelectedPunchCorrections = getSelectedPunchCorrections;
const getSelectedDatePunchTimeSegment = (punchTimeSegments = [], selectedDate) => {
    const filteredPunchTimeSegments = punchTimeSegments.filter((punch) => {
        const punchTime = punch.startDateTime || punch.endDateTime;
        const punchDateTime = punchTime ? luxon_1.DateTime.fromISO(punchTime) : null;
        return punchDateTime && (0, DateManagement_1.areDatesEqual)(selectedDate, punchDateTime);
    });
    return filteredPunchTimeSegments;
};
exports.getSelectedDatePunchTimeSegment = getSelectedDatePunchTimeSegment;
const getFilteredPunchesEligibilityWithTimeRange = (isDeleteDupePunchEnabled, selectedDate, punchTimeSegments = [], timeRange) => {
    const selectedPunches = (0, exports.getSelectedDatePunchTimeSegment)(punchTimeSegments, selectedDate);
    const interval = luxon_1.Interval.fromDateTimes(luxon_1.DateTime.fromISO(timeRange.start), luxon_1.DateTime.fromISO(timeRange.end));
    if (isDeleteDupePunchEnabled && !interval.contains(selectedDate)) {
        return selectedPunches.map((punch) => (0, exports.getFilteredEligibility)(punch));
    }
    return selectedPunches;
};
exports.getFilteredPunchesEligibilityWithTimeRange = getFilteredPunchesEligibilityWithTimeRange;
const getFilteredEligibility = (punch) => {
    return {
        startDateTime: punch.startDateTime,
        endDateTime: punch.endDateTime,
        eligibility: {
            startTimeDeletable: false,
            endTimeDeletable: false
        }
    };
};
exports.getFilteredEligibility = getFilteredEligibility;
